import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';
import { ToastType } from '@components/toast';
import { formVersionQueryKeys } from './keys';
import { formV2AdminKeys } from '@services/formV2Admin/keys';

type UploadTemplateFileResponse = { templateFileId: string };

export const useUploadTemplateFile = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: async ({ formVersionId, file }: { formVersionId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      return client
        .post(`form-version/${formVersionId}/template-file`, {
          body: formData,
        })
        .json<UploadTemplateFileResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [formVersionQueryKeys.resource] });
      queryClient.invalidateQueries({ queryKey: formV2AdminKeys.catalog });

      enqueueSnackbar('Template file uploaded successfully', {
        variant: ToastType.Success,
        preventDuplicate: true,
      });
    },
    onError: async (e) => {
      const message = await getApiErrorMessage(e, 'Failed to upload template file');
      enqueueSnackbar(message, {
        variant: ToastType.Error,
        preventDuplicate: true,
      });
    },
  });

  return { ...mutation, uploadTemplateFile: mutation.mutateAsync };
};
